import { Body, H3 } from "@datapay-by-datacom/design-system";
import type { ComponentParameter, UnmappedElement } from "../types";
import {
  mappingSelectionKey,
  type MappingLayoutMode,
} from "../hooks/useMappingElements";
import type { ComponentInstances } from "../utils/componentAmbiguity";
import {
  findParamByOptionValue,
  getParamOptionValue,
  takenParamOptionValuesExcludingColumn,
} from "../utils/paramOptions";
import styles from "./MappingTablesPanel.module.css";

/** Safe DOM id for section headings (component names may contain spaces). */
function elementHeadingId(sectionKey: string): string {
  return `eh-${sectionKey.replace(/\s+/g, "-")}`;
}

export interface MappingTablesPanelProps {
  mappingMode: MappingLayoutMode;
  onMappingModeChange: (mode: MappingLayoutMode) => void;
  hasSharedElements: boolean;
  controlsDisabled: boolean;
  unknownComponents: string[];
  ambiguousComponents: {
    name: string;
    instances: ComponentInstances;
  }[];
  resolutions: Record<string, string>;
  onSelectInstance: (name: string, companyComponentId: string) => void;
  elements: UnmappedElement[];
  componentNamesByElement: Record<string, string[]>;
  dataColumns: string[];
  selections: Record<string, Record<string, ComponentParameter | null | undefined>>;
  setSelection: (
    scopeKey: string,
    column: string,
    param: ComponentParameter | null,
  ) => void;
  highlightUnmappedScopes: boolean;
  elementsMissingMappings: UnmappedElement[];
  onMappingFieldChange?: () => void;
  showUnknownComponentsBanner?: boolean;
}

export function MappingTablesPanel({
  mappingMode,
  onMappingModeChange,
  hasSharedElements,
  controlsDisabled,
  unknownComponents,
  ambiguousComponents,
  resolutions,
  onSelectInstance,
  elements,
  componentNamesByElement,
  dataColumns,
  selections,
  setSelection,
  highlightUnmappedScopes,
  elementsMissingMappings,
  onMappingFieldChange,
  showUnknownComponentsBanner = true,
}: MappingTablesPanelProps) {
  return (
    <>
      {hasSharedElements && (
        <div
          className={styles.mappingModeBar}
          role="group"
          aria-labelledby="mapping-mode-heading"
        >
          <p id="mapping-mode-heading" className={styles.mappingModeHint}>
            <strong>Components with the same pay element</strong>
          </p>
          <label className={styles.mappingModeLabel}>
            <input
              type="checkbox"
              checked={mappingMode === "individual"}
              disabled={controlsDisabled}
              onChange={(e) => {
                onMappingFieldChange?.();
                onMappingModeChange(e.target.checked ? "individual" : "grouped");
              }}
            />
            Map each component separately (instead of one shared mapping)
          </label>
          <p className={styles.mappingModeHint}>
            {mappingMode === "grouped"
              ? "Components on the same pay element share one column mapping."
              : "Each component has its own column mapping, even when they share a pay element."}
          </p>
        </div>
      )}

      {showUnknownComponentsBanner && unknownComponents.length > 0 && (
        <div className={styles.warningBanner} role="alert">
          <strong>
            {unknownComponents.length} unknown component
            {unknownComponents.length !== 1 ? "s" : ""}:
          </strong>{" "}
          {unknownComponents.join(", ")}. These names don't match any component
          for this company, so their rows can't be mapped and will be rejected
          on import. Fix them in the CSV and re-upload, or continue to import the
          recognised components below.
        </div>
      )}

      {ambiguousComponents.length > 0 && (
        <section
          className={styles.elementSection}
          aria-labelledby="ambiguity-heading"
        >
          <H3 id="ambiguity-heading">Resolve ambiguous components</H3>
          <Body>
            These component names match more than one component for this company.
            Choose which instance to import for each — required before continuing.
          </Body>
          {ambiguousComponents.map(({ name, instances }, index) => {
            const selectId = `ambiguity-select-${index}`;
            return (
              <div key={name} className={styles.ambiguityRow}>
                <label htmlFor={selectId}>{name}</label>
                <select
                  id={selectId}
                  className={styles.paramSelect}
                  value={resolutions[name] ?? ""}
                  disabled={controlsDisabled}
                  onChange={(e) => {
                    onMappingFieldChange?.();
                    onSelectInstance(name, e.target.value);
                  }}
                >
                  <option value="">— choose an instance —</option>
                  {instances.options.map((opt) => (
                    <option
                      key={opt.companyComponentId}
                      value={opt.companyComponentId}
                    >
                      {opt.elementQualifiedName}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </section>
      )}

      {elements.map((element) => {
        const scopeKey = mappingSelectionKey(element, mappingMode);
        const scopeSelections = selections[scopeKey] ?? {};
        const coveredComponents =
          mappingMode === "grouped"
            ? (componentNamesByElement[element.elementQualifiedName] ?? [
                element.componentCode,
              ])
            : [element.componentCode];
        const sectionKey =
          mappingMode === "grouped"
            ? element.elementQualifiedName
            : element.componentCode;
        const isScopeMissingMapping =
          highlightUnmappedScopes &&
          elementsMissingMappings.some(
            (missing) => mappingSelectionKey(missing, mappingMode) === scopeKey,
          );

        return (
          <section
            key={sectionKey}
            className={`${styles.elementSection} ${isScopeMissingMapping ? styles.elementSectionError : ""}`}
            aria-labelledby={elementHeadingId(sectionKey)}
          >
            <H3 id={elementHeadingId(sectionKey)}>
              {mappingMode === "grouped"
                ? coveredComponents.join(", ")
                : element.componentCode}
            </H3>
            {mappingMode === "grouped" && coveredComponents.length > 1 && (
              <Body>
                These components share the same pay element, so one mapping
                applies to all of them.
              </Body>
            )}

            {dataColumns.length === 0 ? (
              <Body>No mappable data columns found in this CSV.</Body>
            ) : (
              <div
                className={styles.tableWrapper}
                role="region"
                aria-label={`Column mappings for ${element.componentCode}`}
                tabIndex={0}
              >
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th scope="col" className={styles.th}>
                        CSV Column
                      </th>
                      <th scope="col" className={styles.th}>
                        Maps to Parameter
                      </th>
                      <th scope="col" className={styles.th}>
                        Type
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataColumns.map((col) => {
                      const currentSel = scopeSelections[col];
                      const selectValue =
                        currentSel === undefined || currentSel === null
                          ? "__skip__"
                          : getParamOptionValue(currentSel);
                      const takenByOtherColumns =
                        takenParamOptionValuesExcludingColumn(
                          scopeSelections,
                          col,
                          dataColumns,
                        );

                      return (
                        <tr key={col}>
                          <td className={styles.tdColumnName}>{col}</td>
                          <td className={styles.tdSelect}>
                            <select
                              className={styles.paramSelect}
                              aria-label={`Parameter for column "${col}" in ${element.componentCode}`}
                              value={selectValue}
                              disabled={controlsDisabled}
                              onChange={(e) => {
                                onMappingFieldChange?.();
                                const val = e.target.value;
                                if (val === "" || val === "__skip__") {
                                  setSelection(scopeKey, col, null);
                                } else {
                                  const param = findParamByOptionValue(
                                    element.parameters,
                                    val,
                                  );
                                  if (param) {
                                    setSelection(scopeKey, col, param);
                                  }
                                }
                              }}
                            >
                              <option value="__skip__">— skip —</option>
                              {element.parameters.map((param) => {
                                const optVal = getParamOptionValue(param);
                                const alreadyMapped =
                                  takenByOtherColumns.has(optVal);
                                const marker = param.isSealed
                                  ? " (sealed — cannot be mapped)"
                                  : alreadyMapped
                                    ? " (mapped to another column)"
                                    : param.requiresReferenceValue
                                      ? " (reference required)"
                                      : "";
                                return (
                                  <option
                                    key={optVal}
                                    value={optVal}
                                    disabled={param.isSealed || alreadyMapped}
                                  >
                                    {param.displayName}
                                    {marker}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className={styles.tdType}>
                            {currentSel != null && currentSel !== undefined ? (
                              <span
                                className={`${styles.typeBadge} ${styles[`type${currentSel.bucketValueType}`]}`}
                              >
                                {currentSel.bucketValueType}
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}
    </>
  );
}
